import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import { OrderSideEnum, TimeInForceEnum } from '../../../lib/enums';
import { OrderTypeEnum } from '../../../lib/enums/order-type.enum';
import lang from '../../../lib/language';
import { Order } from '../../../lib/models/trading/types';
import { RootState } from '../../../lib/store';
import { getCurrentAccount } from '../../../lib/store/crm/helpers';
import { setHasOrderUpdate } from '../../../lib/store/reporting';
import { getCurrentBuyingPower } from '../../../lib/store/selectors';
import MarketStateCache from '../../../lib/store-util/MarketStateCache';
import NBBOPriceCache from '../../../lib/store-util/NBBOPriceCache';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency } from '../../../lib/util/DataHelpers';
import { isMarketClosed, isMarketOpen, isMarketRegularHours } from '../../../lib/util/MarketDataHelpers';
import { TextHelpers } from '../../../lib/util/TextHelpers';
import { uuidv4 } from '../../../lib/util/tools';
import { convertOrderSideToText } from '../../../lib/util/TradingHelpers';
import { getOrderTotalValue } from '../../../lib/util/TradingValidationsHelper';

import useMaintenanceMode from '../../../hooks/useMaintenanceMode';
import useTradingButtonsDisabled from '../../../hooks/useTradingButtonsDisabled';
import Button from '../../../shared_components/Button/Button';
import CommonDropdown from '../../../shared_components/CommonDropdown/CommonDropdown';
import { Error, MaintenanceNotice, NotEnoughBuyingPower, ReviewOrder } from '../..';
import HighLowLimitPriceAlert from '../../HighLowLimitPriceAlert/HighLowLimitPriceAlert';
import SharesCalculator from '../../SharesCalculator/SharesCalculator';
import LimitPriceInput from '../components/LimitPriceInput';
import MarketPriceInfo from '../components/MarketPriceInfo';
import { OrderTypeDropdownOptions } from '../order.constants';
import { getNameByOrderType, getValidationPrice } from '../OrderHelpers';
import useValidateOrder from '../useValidateOrder';

import styles from './CreateOrder.module.scss';

type OwnProps = {
  symbol: string;
  orderSide: string;
  positionsQuantity: number | null;
  isPopupMode?: boolean;
  hasMyStocksOrderUpdate?: boolean;
  isMyAccount?: boolean;
  setOrderInfo: (order: Order) => void;
  setFromReview: (fromReview: boolean) => void;
}
type Props = OwnProps & ReduxProps;

function CreateOrder(props: Props) {
  const {
    symbol = '',
    account,
    orderSide,
    positionsQuantity,
    isPopupMode,
    buyingPow,
    hasMyStocksOrderUpdate,
    accountOpenPositionsCompleted,
    isMyAccount,
    setOrderInfo,
    setFromReview,
    myStocksUpdate,
    accountStatusData,
  } = props;

  const isBuyOrder = orderSide === OrderSideEnum.Buy;

  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);
  const marketClosed = isMarketClosed(marketState);
  const showMarketInfoIcon = isMarketOpen(marketState);
  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });
  const isMaintenance = useMaintenanceMode();
  const nbboData = NBBOPriceCache.use(symbol);
  const { askprice, bidprice } = nbboData;

  const [order, setOrder] = useState<Order>({});
  const [reviewOrder, setReviewOrder] = useState<boolean>(false);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [criticalError, setCriticalError] = useState<string>('');
  const [targetPriceError, setTargetPriceError] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [targetPrice, setTargetPrice] = useState<number>(0);
  const [orderType, setOrderType] = useState<string>(isMarketHours ? OrderTypeEnum.Market : OrderTypeEnum.Limit);
  const [timeInForce, setTimeInForce] = useState<string>(TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay));

  const [isReviewOrderCheckTriggered, setIsReviewOrderCheckTriggered] = useState<boolean>(false);
  const [openedEnoughBPModal, setOpenedEnoughBPModal] = useState<boolean>(false);
  const [visibleHighLowLimitPriceAlert, setVisibleHighLowLimitPriceAlert] = useState<boolean>(false);

  const isMarketOrder = orderType === OrderTypeEnum.Market;
  const orderTypeOptions = OrderTypeDropdownOptions(isBuyOrder);
  const orderTypeDropdownOptions = orderTypeOptions.map(option => ({
    id: String(option.value),
    label: option.text ?? '',
    icon: option.icon,
    subText: option.subText,
  }));
  const { currentPrice: tradePrice = 0 } = TradePriceCache.use(symbol, 'CreateOrder');
  const validationPirce = getValidationPrice(marketClosed, askprice, bidprice, tradePrice, isBuyOrder);
  const { errorMessage, controlCheck } = useValidateOrder(orderType, validationPirce ?? 0, targetPrice, isBuyOrder);

  const totalValue = getOrderTotalValue(
    orderType,
    quantity,
    tradePrice,
    targetPrice,
  );

  const isBuyingPowerEnough = buyingPow > totalValue;

  useEffect(() => {
    if (isReviewOrderCheckTriggered && ((accountOpenPositionsCompleted && !marketClosed) || marketClosed)) {
      setFromReview(true);
      setReviewOrder(false);
      setIsReviewOrderCheckTriggered(false);
    }
  }, [order, setFromReview, isReviewOrderCheckTriggered, accountOpenPositionsCompleted, marketClosed]);

  const setOrderData = useCallback((theOrder: Order) => {
    setOrder(theOrder);
    setOrderInfo(theOrder);
    setReviewOrder(true);
  }, [setOrderInfo]);

  const isValidOrder = useCallback((qty: number, buyingPower: number): boolean => {
    setTargetPriceError(errorMessage);

    const buyingPowerCheck = buyingPower > totalValue;

    if (!positionsQuantity && (controlCheck && !buyingPowerCheck)) {
      setOpenedEnoughBPModal(true);
    }

    if (positionsQuantity) {
      return (positionsQuantity >= qty) && controlCheck;
    }

    return buyingPowerCheck && controlCheck;
  }, [controlCheck, errorMessage, positionsQuantity, totalValue]);

  const onReviewOrderClicked = useCallback((
    sym: string,
    qty: number,
    type: string,
    price: number,
    side: string,
    time: string,
    buyingPower: number,
    target: number,
  ) => {
    setCriticalError('');
    setTargetPriceError('');

    if (isValidOrder(qty, buyingPower)) {
      const orderObject: Order = {};

      if (type === OrderTypeEnum.Stop) {
        orderObject.stopPrice = target;
      }

      orderObject.clientOrderId = uuidv4();
      orderObject.symbol = sym;
      orderObject.side = side as OrderSideEnum;
      orderObject.orderQty = qty;
      orderObject.ordType = type as OrderTypeEnum;
      if (type !== OrderTypeEnum.Market) {
        orderObject.price = target;
      }
      orderObject.account = account;

      if (type === OrderTypeEnum.Limit && askprice && bidprice) {
        if (
          (isBuyOrder && (targetPrice > askprice))
          || (!isBuyOrder && (targetPrice < bidprice))
        ) {
          setVisibleHighLowLimitPriceAlert(isBuyOrder ? (targetPrice > askprice) : (targetPrice < bidprice));
          setOrder(orderObject);
          setOrderInfo(orderObject);

          return;
        }
      }
      setOrderData(orderObject);
    }
  }, [
    askprice,
    bidprice,
    targetPrice,
    account,
    isBuyOrder,
    isValidOrder,
    setOrderData,
    setOrderInfo,
  ]);

  const handleChangeOrderType = useCallback((option) => {
    setTargetPriceError('');
    setOrderType(option.value);
    setTimeInForce(TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay));
  }, []);

  useEffect(() => {
    positionsQuantity ? setQuantity(positionsQuantity) : setQuantity(1);
  }, [positionsQuantity, orderSide]);

  useEffect(() => {
    if (!isInitialised && positionsQuantity) {
      setQuantity(positionsQuantity);
      setIsInitialised(true);
    }
  }, [isInitialised, positionsQuantity]);

  return (
    <>
      <div className={styles.wrapperOrderContainer}>
        {isPopupMode && <h6 className={styles.title}>{`${convertOrderSideToText(orderSide)} ${symbol}`}</h6>}
        <div className={styles.wrapperInfo}>
          <div className={styles.wrapperOrderTypeDropDown}>
            <p className={styles.orderTypeText}>{lang.commonTradingOrderType()}</p>
            <div className={styles.orderTypeDropDown}>
              <CommonDropdown
                options={orderTypeDropdownOptions}
                selectedOption={{ id: orderType, label: getNameByOrderType(orderType) }}
                onSelectOption={option => handleChangeOrderType({ value: option.id })}
                title={lang.commonTradingOrderType()}
                isDisabled={isButtonsDisabled}
                menuClassName={styles.orderTypeDropdownMenu}
                itemClassName={styles.orderTypeOption}
              />
            </div>
          </div>
          <div className={styles.wrapperNumberOfShares}>
            <div className={styles.numberOfShares}>
              <p className={styles.numberOfSharesText}>{lang.mobileTradingTicketNumberOfShares()}</p>
              {!!positionsQuantity && (
                <p className={styles.numberOfSharesSubText}>
                  {
                    isBuyOrder
                      ? lang.webTradingShortedStocks(positionsQuantity)
                      : lang.webTradingAvailableShares(positionsQuantity)
                  }
                </p>
              )}
            </div>
            <div className={styles.wrapperSharesField}>
              <SharesCalculator
                quantity={quantity}
                setQuantity={setQuantity}
                maxQuantity={positionsQuantity || undefined}
              />
            </div>
          </div>
          {(!isMarketOrder) && (
            <LimitPriceInput
              initialPrice={0}
              orderType={orderType}
              targetPriceError={targetPriceError}
              setTargetPrice={(value) => setTargetPrice(value)}
              setTargetPriceError={(value) => setTargetPriceError(value)}
            />
          )}
          <MarketPriceInfo
            askPrice={askprice}
            bidPrice={bidprice}
            symbol={symbol}
            tradePrice={tradePrice}
            isMarketOpen={showMarketInfoIcon}
            isMyAccount={isMyAccount}
          />
          {(!isMarketOrder) && (
            <div className={styles.expiryDateContainer}>
              <div className={styles.expiryDateText}>
                {lang.headerMyAccountPendingOrderExpiryDate2()}
              </div>
              <div className={styles.rightAligned}>
                {TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay)}
              </div>
            </div>
          )}
          <div className={styles.horizontalLine} />
          <div className={classNames(styles.totalValueContainer, { [styles.noBuyingPower]: positionsQuantity })}>
            <div>{lang.commonTradingOrderValue()}</div>
            <div className={
              classNames(styles.value,
                { [styles.redValue]: !isBuyingPowerEnough && !positionsQuantity })
            }
            >
              {formatCurrency(totalValue)}
            </div>
          </div>
          {!positionsQuantity && (
            <div className={styles.availableBuyingContainer}>
              <div>{lang.mobileOrderCreateBuyingPower()}</div>
              <div className={styles.value}>{formatCurrency(buyingPow)}</div>
            </div>
          )}
          {(criticalError.trim() !== '') && (
            <div className={styles.errorWrapper}>
              <Error
                hiddenIcon
                errorMessage={criticalError}
              />
            </div>
          )}

          {isMaintenance ? <MaintenanceNotice /> : (
            <Button
              className={styles.buttonOrder}
              disabled={quantity <= 0 || isButtonsDisabled}
              onClick={() =>
                onReviewOrderClicked(
                  symbol,
                  quantity,
                  orderType,
                  tradePrice ?? 0,
                  orderSide,
                  timeInForce,
                  buyingPow,
                  targetPrice,
                )}
            >
              {lang.mobileOrderCreateButtonReviewOrder()}
            </Button>
          )}
          {openedEnoughBPModal && <NotEnoughBuyingPower closeModal={() => setOpenedEnoughBPModal(false)} />}
          {visibleHighLowLimitPriceAlert && (
            <HighLowLimitPriceAlert
              isBuyOrder={isBuyOrder}
              processModal={() => {
                setVisibleHighLowLimitPriceAlert(false);
                setReviewOrder(true);
              }}
              toggleModal={() => setVisibleHighLowLimitPriceAlert(false)}
            />
          )}
        </div>
      </div>
      {reviewOrder && (
        <ReviewOrder
          symbol={symbol}
          order={order}
          isBuyOrder={isBuyOrder}
          onSubmit={() => {
            if (!marketClosed) {
              hasMyStocksOrderUpdate && myStocksUpdate(true);
            }
            setIsReviewOrderCheckTriggered(true);
          }}
          onCancel={() => setReviewOrder(false)}
        />
      )}
    </>
  );
}

const mapStateProps = (state: RootState) => ({
  account: getCurrentAccount(state),
  accountOpenPositionsCompleted: state.reporting.accountOpenPositionsCompleted,
  buyingPow: getCurrentBuyingPower(state),
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CreateOrder);
